import { ModernSelect, SelectOption } from "@components/Select";
import {
  getDefaultFormControlSx,
  getDefaultSelectSx
} from "@components/Select/ModernSelect";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CancelIcon from "@mui/icons-material/Cancel";
import KeyIcon from "@mui/icons-material/Key";
import KeyOffIcon from "@mui/icons-material/KeyOff";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SaveIcon from "@mui/icons-material/Save";
import VerifiedIcon from "@mui/icons-material/Verified";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useClients, useLocalizationSettings } from "@services/api";
import { T_BOOL_FIELD, UserCreate } from "@services/api/types";
import { getTextFieldBasicSx, getTextFieldSx } from "@styles/TextField";
import { validateEmail } from "@utils/email";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      borderRadius: 2,
      mt: 1,
      boxShadow: "0 1px 4px #2645F5"
    }
  }
};

interface UserFormProps {
  mode: "create" | "update";
  user: UserCreate;
  onSave: (event: any) => void;
  onGoBack: () => void;
  processing: boolean;
  loadingUser?: boolean;
  handleFieldChange: (field: keyof UserCreate, value: any) => void;
  handleBoolFieldChanged: (field: T_BOOL_FIELD, value: boolean) => void;
  handleClientsChange: (event: SelectChangeEvent) => void;
}

const UserForm = ({
  mode,
  user,
  onSave,
  onGoBack,
  processing,
  loadingUser = false,
  handleFieldChange,
  handleBoolFieldChanged,
  handleClientsChange
}: UserFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [textFieldHeight, setTextFieldHeight] = useState<number>(56); // Default MUI TextField input height
  const emailInputRef = useRef<HTMLInputElement>(null);

  const isSmallScreen = useMemo(
    () => isMobile || isTablet,
    [isMobile, isTablet]
  );

  const { data: clients, isFetching: isLoadingClients } = useClients();
  const {
    data: localizationSettings,
    isFetching: isLoadingLocalizationSettings
  } = useLocalizationSettings();

  useEffect(() => {
    if (emailInputRef.current) {
      const height = emailInputRef.current.offsetHeight;
      if (height > 0) {
        setTextFieldHeight(height);
      }
    }
  }, []);

  const clientOptions = useMemo(() => {
    return (clients || []).map((client) => (
      <MenuItem key={`user-client-${client.id}`} value={client.id}>
        {client.name}
      </MenuItem>
    ));
  }, [clients]);

  const clientsNameById = useMemo(() => {
    return (clients || []).reduce(
      (acc, client) => {
        acc[client.id] = client.name;
        return acc;
      },
      {} as Record<number, string>
    );
  }, [clients]);

  const languageOptions = useMemo(() => {
    return (localizationSettings?.options || []).reduce((acc, option) => {
      acc.push({
        value: option,
        label: t(`newUser.localization.${option}`)
      });
      return acc;
    }, [] as SelectOption[]);
  }, [localizationSettings, t]);

  const isFormValid = useMemo(() => {
    const passwordValid =
      user.password.length > 0 &&
      passwordConfirmation.length > 0 &&
      passwordConfirmation === user.password;

    const passwordModeValid =
      mode === "create"
        ? passwordValid
        : user.password.length > 0
          ? passwordValid
          : true;

    return (
      user.email.length > 0 &&
      validateEmail(user.email) &&
      user.username.length > 0 &&
      user.last_name.length > 0 &&
      user.first_name.length > 0 &&
      passwordModeValid
    );
  }, [
    user.email,
    user.username,
    user.last_name,
    user.first_name,
    user.password,
    passwordConfirmation
  ]);

  const isLoading = useMemo(() => {
    return isLoadingClients || isLoadingLocalizationSettings || loadingUser;
  }, [isLoadingClients, isLoadingLocalizationSettings, loadingUser]);

  const textFieldSx = useMemo(() => getTextFieldSx(theme), [theme]);
  const textFieldBasicSx = useMemo(() => getTextFieldBasicSx(theme), [theme]);

  const isUpdateMode = useMemo(() => mode === "update", [mode]);

  return (
    <Box
      component="form"
      sx={{
        "& .MuiTextField-root": { p: 1 },
        width: "100%",
        height: "100%"
      }}
      noValidate
      autoComplete="off"
      className="p-8"
      onSubmit={onSave}
    >
      <Grid container spacing={2} justifyContent="center">
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center" }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newUser.personal_information")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          {loadingUser ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <Box className="flex flex-row items-center gap-2">
              <Tooltip
                title={t(
                  user.is_email_verified
                    ? "user.verified_email"
                    : "user.not_verified_email"
                )}
              >
                <Box className="flex flex-row items-center gap-2">
                  <VerifiedIcon
                    color={user.is_email_verified ? "success" : "error"}
                  />
                  <Switch
                    checked={user.is_email_verified}
                    onChange={(event) =>
                      handleBoolFieldChanged(
                        "is_email_verified",
                        event.target.checked
                      )
                    }
                    disabled={!validateEmail(user.email)}
                  />
                </Box>
              </Tooltip>
              <TextField
                inputRef={emailInputRef}
                id="new-user-email"
                key="new-user-email"
                label={t("newClient.email")}
                color="info"
                value={user.email}
                onChange={(event) =>
                  handleFieldChange("email", event.target.value)
                }
                error={!validateEmail(user.email)}
                helperText={
                  !validateEmail(user.email) ? t("newClient.emailError") : ""
                }
                sx={{ ...textFieldSx }}
                required
                fullWidth
              />
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          {loadingUser ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <Box className="flex flex-row items-center gap-2">
              <Tooltip
                title={t(
                  user.enabled ? "user.enabled_user" : "user.disabled_user"
                )}
              >
                <Box className="flex flex-row items-center gap-2">
                  {user.enabled && <PersonIcon color="success" />}
                  {!user.enabled && <PersonOffIcon color="error" />}
                  <Switch
                    checked={user.enabled}
                    onChange={(event) =>
                      handleBoolFieldChanged("enabled", event.target.checked)
                    }
                    disabled={user.username.length === 0}
                  />
                </Box>
              </Tooltip>
              <TextField
                id="new-user-name"
                key="new-user-name"
                label={t("users.username")}
                color="info"
                value={user.username}
                onChange={(event) =>
                  handleFieldChange("username", event.target.value)
                }
                required
                fullWidth
                disabled={isUpdateMode}
                sx={isUpdateMode ? { ...textFieldBasicSx } : { ...textFieldSx }}
              />
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          {loadingUser ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <TextField
              id="new-user-last-name"
              key="new-user-last-name"
              label={t("users.last_name")}
              color="info"
              value={user.last_name}
              onChange={(event) =>
                handleFieldChange("last_name", event.target.value)
              }
              sx={{ ...textFieldSx }}
              required
              fullWidth
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          {loadingUser ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <TextField
              id="new-user-first-name"
              key="new-user-first-name"
              label={t("users.first_name")}
              color="info"
              value={user.first_name}
              onChange={(event) =>
                handleFieldChange("first_name", event.target.value)
              }
              sx={{ ...textFieldSx }}
              required
              fullWidth
            />
          )}
        </Grid>
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center" }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newUser.security_credentials")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 2, md: 2 }} sx={{ justifyContent: "center" }}>
          <Tooltip
            title={t(
              user.is_password_temporary
                ? "newUser.temporary_password"
                : "newUser.not_temporary_password"
            )}
          >
            <Box className="flex flex-row items-center gap-2">
              {user.is_password_temporary ? (
                <KeyOffIcon color="error" />
              ) : (
                <KeyIcon color="success" />
              )}
              <Switch
                checked={user.is_password_temporary}
                onChange={(event) =>
                  handleBoolFieldChanged(
                    "is_password_temporary",
                    event.target.checked
                  )
                }
                disabled={user.password.length === 0}
              />
            </Box>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 12, sm: 5, md: 5 }} sx={{ justifyContent: "center" }}>
          <TextField
            id="new-user-password"
            key="new-user-password"
            label={t("users.password")}
            color="info"
            value={user.password}
            onChange={(event) =>
              handleFieldChange("password", event.target.value)
            }
            required={mode === "create"}
            fullWidth
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ ...textFieldSx }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 5, md: 5 }} sx={{ justifyContent: "center" }}>
          <TextField
            id="new-user-password-confirmation"
            key="new-user-password-confirmation"
            label={t("users.password_confirmation")}
            color="info"
            value={passwordConfirmation}
            onChange={(event) => setPasswordConfirmation(event.target.value)}
            error={passwordConfirmation !== user.password}
            helperText={
              passwordConfirmation !== user.password
                ? t("users.password_confirmation_error")
                : ""
            }
            required={mode === "create"}
            fullWidth
            type={showPasswordConfirmation ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowPasswordConfirmation(!showPasswordConfirmation)
                    }
                  >
                    {showPasswordConfirmation ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ ...textFieldSx }}
          />
        </Grid>
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center" }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newUser.access_section")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 1, md: 1 }} sx={{ justifyContent: "center" }}>
          {loadingUser ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <Box className="flex flex-row items-center gap-2">
              <Tooltip
                title={t(user.is_admin ? "user.is_admin" : "user.is_not_admin")}
              >
                <Box className="flex flex-row items-center gap-2">
                  <AdminPanelSettingsIcon
                    color={user.is_admin ? "success" : "error"}
                  />

                  <Switch
                    checked={user.is_admin}
                    onChange={(event) =>
                      handleBoolFieldChanged("is_admin", event.target.checked)
                    }
                  />
                </Box>
              </Tooltip>
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          {isLoadingLocalizationSettings ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <Box className="mt-4">
              <ModernSelect
                options={languageOptions}
                value={user.locale}
                onChange={(event) =>
                  handleFieldChange("locale", event.target.value)
                }
                valueKey="id"
                labelKey="name"
                showIndicatorDot={true}
                colorTheme="light"
                id="user-language-select-helper"
                label={`${t("newUser.language")} *`}
                disabled={processing}
                sx={{
                  marginBottom: 2,
                  marginLeft: 1,
                  marginRight: 1,
                  marginTop: 1,
                  height: textFieldHeight
                }}
              />
            </Box>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 8, md: 8 }} sx={{ justifyContent: "center" }}>
          {isLoadingClients ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <Box className="mt-4">
              <FormControl
                fullWidth
                sx={{
                  ...getDefaultFormControlSx("light", undefined, false, theme)
                }}
              >
                <InputLabel id="user-client-select-helper-label">{`${t("newUser.clients")} *`}</InputLabel>
                <Select
                  labelId="user-client-select-helper-label"
                  id="user-client-select-helper"
                  value={user.clients}
                  label={`${t("newUser.clients")} *`}
                  // @ts-expect-error TODO: fix this
                  onChange={handleClientsChange}
                  multiple
                  input={
                    <OutlinedInput id="select-multiple-chip" label="Chip" />
                  }
                  renderValue={(selected) => {
                    if (isSmallScreen) {
                      return `${selected.length} ${t("newUser.selected")}`;
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={clientsNameById[value]} />
                        ))}
                      </Box>
                    );
                  }}
                  MenuProps={MenuProps}
                  className="ml-2 mr-2"
                  disabled={user.is_admin}
                  sx={{
                    ...getDefaultSelectSx("light", "medium", theme),
                    height: textFieldHeight,
                    marginTop: 1
                  }}
                >
                  {clientOptions}
                </Select>
              </FormControl>
            </Box>
          )}
        </Grid>
      </Grid>
      <Box className="flex justify-end p-8 gap-4">
        <Tooltip title={t("common.discard")}>
          <IconButton color="error" onClick={onGoBack} disabled={processing}>
            <CancelIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("common.save")}>
          <IconButton
            color="success"
            type="submit"
            disabled={processing || !isFormValid || isLoading}
          >
            <SaveIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default UserForm;
